import React, { useEffect, useMemo, useState } from "react";
import "./App.css";

type Page = "home" | "contact";

function getPageFromPath(): Page {
  const path = window.location.pathname.toLowerCase();

  if (path.includes("contact")) return "contact";

  return "home";
}

function App() {
  const [page, setPage] = useState<Page>(getPageFromPath());
  const [isScrolled, setIsScrolled] = useState(false);
  const [formSent, setFormSent] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    const revealElements = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -40px 0px",
      }
    );

    revealElements.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener("scroll", handleScroll);
      revealElements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [page]);

  useEffect(() => {
    const handlePopState = () => setPage(getPageFromPath());
    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (targetPage: Page) => {
    const pathMap: Record<Page, string> = {
      home: "/",
      contact: "/contact-us",
    };

    window.history.pushState(null, "", pathMap[targetPage]);
    setPage(targetPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageContent = useMemo(() => {
    if (page === "contact") {
      return <ContactPage />;
    }

    return <HomePage navigate={navigate} />;
  }, [page]);

  return (
    <>
      <Navbar page={page} navigate={navigate} isScrolled={isScrolled} />
      {pageContent}
      <Footer navigate={navigate} />
    </>
  );
}

type NavProps = {
  page: Page;
  navigate: (page: Page) => void;
  isScrolled: boolean;
};

function Navbar({ page, navigate, isScrolled }: NavProps) {
  return (
    <header className={`site-nav ${isScrolled ? "scrolled" : ""}`}>
      <button
        className="brand"
        onClick={() => navigate("home")}
        aria-label="OmniMarkets"
      >
        <OmniRisksLogo />
      </button>

      <nav className="nav-links" aria-label="Main navigation">
        <button
          className={page === "home" ? "active" : ""}
          onClick={() => navigate("home")}
        >
          Home
        </button>

        <button
          className={page === "contact" ? "active" : ""}
          onClick={() => navigate("contact")}
        >
          Contact Us
        </button>

        <a
          href="https://www.om-ft.com"
          target="_blank"
          rel="noreferrer"
          className="nav-external-link"
        >
          OM-FT
        </a>
      </nav>
    </header>
  );
}

function OmniRisksLogo() {
  return (
    <img
      src="/assets/Logo.png"
      alt="OmniMarkets"
      className="omnirisks-logo-image"
    />
  );
}

type HomeProps = {
  navigate: (page: Page) => void;
};

function HomePage({ navigate }: HomeProps) {
  return (
    <main className="home-page">
      <section className="home-hero">
        <NetworkBackground />

        <div className="home-hero-inner">
          <div className="home-hero-copy reveal">
            <div className="eyebrow">
              Risk Management · Financial Technology · Regulatory Solutions
            </div>

            <h1>
              RegTech
              <br />
              <span>Next-Generation Solutions</span>
            </h1>

            <p>A comprehensive platform for your quantitative model needs.</p>

            <div className="hero-actions">
              {/* <button
                className="outline-button"
                onClick={() =>
                  document
                    .getElementById("market-transformation")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Learn More <span>›</span>
              </button> */}

              <button className="ghost-link-button" onClick={() => navigate("contact")}>
                Contact Us
              </button>
            </div>
          </div>

          <div className="home-hero-panel reveal reveal-delay-1">
            <div className="panel-label">Core Capabilities</div>

            <div className="focus-list">
              <div>
                <span>01</span>
                <strong>Model Risk Management</strong>
                <p>
                  Since SR 11-7, we have developed a deep experience on MRM topics:
                  MRM platforms, inventories, policies, developments, implementations, validations, on-going monitoring, and reporting.
                </p>
              </div>

              <div>
                <span>02</span>
                <strong>Margins</strong>
                <p>
                  We have worked on the most widely used margin models (e.g. SPAN and TIMS for F&O)
                  but also on SIMM for uncleared derivatives since 2018.
                </p>
              </div>

              <div>
                <span>03</span>
                <strong>Pricing & Risk Infrastructure</strong>
                <p>
                  Most of our work has focused on building robust pricing and risk infrastructure to support complex financial models,
                  business and regulatory requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <section id="market-transformation" className="market-transformation">
        <div className="market-inner">
          <div className="section-tag reveal">
            <div className="tag-line"></div>
            <span>Market Transformation</span>
          </div>

          <div className="market-grid">
            <h2 className="reveal reveal-delay-1">
              Responding to the changing
              <em> landscapes of derivatives</em>.
            </h2>

            <div className="market-copy reveal reveal-delay-2">
              <p>
                The derivatives markets are currently undergoing historical business
                and regulatory changes while adapting to the digital age. The new
                challenges are not marginal but transformative.
              </p>

              <p>
                We are developing a full suite of modular solutions to help you meet
                all your derivatives challenges, transform your workflows and meet
                your new increased reporting and regulatory requirements.
              </p>
            </div>
          </div>
        </div>
      </section> */}

      <section className="services-showcase">
        <NetworkBackground variant="soft" />

        <div className="services-showcase-inner">
          <div className="section-tag center reveal">
            <div className="tag-line"></div>
            <span>Our Services</span>
            <div className="tag-line"></div>
          </div>

          <div className="services-cards">
            {/* <article className="service-feature-card reveal">
              <div className="service-art service-art-one"></div>

              <div className="service-feature-copy">
                <p>
                  Comprehensive workflow solutions that cover your business needs and
                  regulatory requirements.
                </p>

                <h3>Suites & Apps</h3>

                <button className="outline-button small" onClick={() => navigate("contact")}>
                  Discover More <span>›</span>
                </button>
              </div>
            </article> */}

            <article className="service-feature-card reveal reveal-delay-1">
              <div className="service-art service-art-two"></div>

              <div className="service-feature-copy">
                <p>Customized solutions for pricing and risk-related topics.</p>

                <h3>Consulting Services</h3>

                <button className="outline-button small" onClick={() => navigate("contact")}>
                  Discover More <span>›</span>
                </button>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="omft-section">
        <div className="omft-inner">
          <div className="omft-card reveal">
            <div className="platform-kicker">Financial Technology Platform</div>

            <h2>
              OmniMarkets
              <br />
              <span>Financial Technologies</span>
            </h2>

            <p>
              OmniMarkets Financial Technologies builds on OmniMarkets' experience on institutional-grade financial infrastructure
              and business workflows to realize our vision of solutions designed for governance, transparency, and control.
            </p>

            <div className="platform-actions">
              <a
                href="https://www.om-ft.com"
                target="_blank"
                rel="noreferrer"
                className="outline-button small as-link"
              >
                Visit OM-FT <span></span>
              </a>

              {/* <button className="ghost-link-button" onClick={() => navigate("contact")}>
                Group Inquiry
              </button> */}
            </div>
          </div>

          {/* <div className="omft-side reveal reveal-delay-1">
            <div className="side-card">
              <span>01</span>
              <h3>RegTech Solutions</h3>
              <p>
                Technology-enabled solutions for regulatory change, reporting pressure,
                and financial workflow modernization.
              </p>
            </div>

            <div className="side-card">
              <span>02</span>
              <h3>Quantitative Infrastructure</h3>
              <p>
                Model-aware software and advisory support for derivatives, pricing,
                risk, and analytics.
              </p>
            </div>

            <div className="side-card">
              <span>03</span>
              <h3>Institutional Delivery</h3>
              <p>
                Practical implementation support for firms that need reliable,
                transparent, and scalable technology.
              </p>
            </div>
          </div> */}
        </div>
      </section>

      {/* <section className="our-role">
        <NetworkBackground variant="soft" />

        <div className="role-inner">
          <div className="role-heading reveal">
            <div className="section-tag">
              <div className="tag-line"></div>
              <span>Our Role</span>
            </div>

            <h2>
              From market change to implementation,
              <br />
              OmniRisks connects risk,
              <em> technology</em>, and execution.
            </h2>
          </div>

          <div className="role-steps">
            <div className="role-step reveal">
              <span>01</span>
              <h3>Understand</h3>
              <p>
                Analyze business, regulatory, and model-driven challenges facing
                financial institutions.
              </p>
            </div>

            <div className="role-step reveal reveal-delay-1">
              <span>02</span>
              <h3>Structure</h3>
              <p>
                Translate complex requirements into modular solutions and clear workflow
                architecture.
              </p>
            </div>

            <div className="role-step reveal reveal-delay-2">
              <span>03</span>
              <h3>Build</h3>
              <p>
                Develop practical tools and platforms that support pricing, risk,
                reporting, and operations.
              </p>
            </div>

            <div className="role-step reveal reveal-delay-3">
              <span>04</span>
              <h3>Support</h3>
              <p>
                Help institutions adapt continuously as markets, regulations, and
                technology evolve.
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* <section className="home-cta">
        <div className="home-cta-inner reveal">
          <div className="section-tag center">
            <div className="tag-line"></div>
            <span>Connect</span>
            <div className="tag-line"></div>
          </div>

          <h2>
            Looking to modernize risk,
            <br />
            regulation, or financial workflows?
          </h2>

          <p>
            OmniRisks works with institutions, technology teams, and market
            practitioners to build practical solutions for complex financial
            environments.
          </p>

          <button className="outline-button" onClick={() => navigate("contact")}>
            Get Started <span>›</span>
          </button>
        </div>
      </section> */}
    </main>
  );
}


function ContactPage() {
  const [formSent, setFormSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    position: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const response = await fetch(
        "https://omft-contact-ckdjcwhtgzd5fcfq.eastus-01.azurewebsites.net/api/Contact/OmniMarkets",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send contact form.");
      }

      setFormSent(true);

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        position: "",
        message: "",
      });

      setTimeout(() => { setFormSent(false); }, 3000);
    } catch (error) {
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitted = formSent;
  const submitText = isSubmitted
    ? "Sent ✓"
    : isSubmitting
      ? "Sending..."
      : "Send";

  return (
    <main className="page-main contact-page">
      <NetworkBackground />

      <section className="page-panel reveal">
        <div className="page-heading">
          <h1>Contact Us</h1>
          <div className="heading-line"></div>
        </div>

        <div className="contact-layout">
          <div className="contact-details">
            <ContactInfo icon="linkedin" label="LinkedIn" />
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label>
                First Name *
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                Last Name *
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <label>
              Email *
              <input
                type="email"
                name="email"
                placeholder="Company Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Company *
              <input
                type="text"
                name="company"
                placeholder="Company"
                value={formData.company}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Position *
              <input
                type="text"
                name="position"
                placeholder="Position"
                value={formData.position}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Message *
              <textarea
                name="message"
                placeholder="Add your message here..."
                value={formData.message}
                onChange={handleChange}
                required
              />
            </label>

            <button
              type="submit"
              className="send-button"
              disabled={isSubmitting || isSubmitted}
            >
              {submitText}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

type ContactInfoProps = {
  icon: "phone" | "mail" | "linkedin";
  label: string;
};

function ContactInfo({ icon, label }: ContactInfoProps) {
  if (icon === "linkedin") {
    return (
      <div className="contact-info-item">
        <div className="contact-icon">in</div>
        <a
          href="https://www.linkedin.com/company/omnimarkets"
          target="_blank"
          rel="noreferrer"
          className="contact-link"
        >
          {label}
        </a>
      </div>
    );
  }

  return (
    <div className="contact-info-item">
      <div className="contact-icon">
        {icon === "phone" && "☎"}
        {icon === "mail" && "✉"}
      </div>
      <span>{label}</span>
    </div>
  );
}


type FooterProps = {
  navigate: (page: Page) => void;
};

function Footer({ navigate }: FooterProps) {
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div className="footer-brand-col">
          <button
            className="brand footer-brand"
            onClick={() => navigate("home")}
            aria-label="OmniMarkets"
          >
            <OmniRisksLogo />
          </button>

          <p>
            Technology-enabled risk management and regulatory solutions for
            financial institutions operating in complex markets.
          </p>
        </div>

        <div className="footer-col">
          <h4>Company</h4>
          <button onClick={() => navigate("home")}>Home</button>
          <button onClick={() => navigate("contact")}>Contact Us</button>
        </div>

        <div className="footer-col">
          <h4>Platforms</h4>
          <a href="https://www.om-ft.com" target="_blank" rel="noreferrer">
            OmniMarkets Financial Technologies
          </a>
        </div>

        {/* <div className="footer-col">
          <h4>Contact Us</h4>

          <a
            href="https://www.linkedin.com/company/omnimarkets"
            target="_blank"
            rel="noreferrer"
            className="footer-linkedin"
            aria-label="OmniMarkets LinkedIn"
          >
            LinkedIn
          </a>
        </div> */}
      </div>

      <div className="footer-bottom">
        <span>© 2026 OmniMarkets. All rights reserved.</span>
        <span>Risk Management · Financial Technology · Regulatory Solutions</span>
      </div>
    </footer>
  );
}

type NetworkBackgroundProps = {
  variant?: "default" | "soft" | "lines" | "mesh";
};

function NetworkBackground({ variant = "default" }: NetworkBackgroundProps) {
  return (
    <div className={`network-bg network-${variant}`} aria-hidden="true">
      <div className="wave wave-one"></div>
      <div className="wave wave-two"></div>
      <div className="dot-field"></div>
      <div className="grid-glow"></div>
    </div>
  );
}

export default App;